import { useEffect, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  tableWrap: {
    maxHeight: 420,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 800, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    background: "#07111f",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: {
    fontSize: 10,
    padding: "4px 7px",
    borderRadius: 20,
    fontWeight: 600,
  },
};

const emptyForm = {
  accountCode: "",
  accountName: "",
  accountType: "Asset",
  parentAccountId: "",
  isActive: true,
};

export default function AccountsPage() {
  const { user } = useAuth();
  const companyId = Number(user?.companyId || 0);

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const getId = (x) => x?.Id ?? x?.id;
  const getCode = (x) => x?.AccountCode ?? x?.accountCode ?? "";
  const getName = (x) => x?.AccountName ?? x?.accountName ?? "";
  const getType = (x) => x?.AccountType ?? x?.accountType ?? "";
  const getParentId = (x) => x?.ParentAccountId ?? x?.parentAccountId;
  const getIsActive = (x) => x?.IsActive ?? x?.isActive ?? true;

  const load = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await client.get("/accounts", {
        params: companyId > 0 ? { companyId } : {},
      });

      console.log("Accounts response:", res.data);

      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.data || res.data?.result || [];

      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Accounts load error:", e);

      setMessage(
        e?.response?.data?.message ||
          e?.response?.data?.title ||
          e?.response?.data ||
          e.message ||
          "Unable to load accounts.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const save = async () => {
    if (!form.accountCode.trim() || !form.accountName.trim()) {
      alert("Please fill Account Code and Account Name");
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const payload = {
        accountCode: form.accountCode.trim(),
        accountName: form.accountName.trim(),
        accountType: form.accountType,
        parentAccountId: form.parentAccountId
          ? Number(form.parentAccountId)
          : null,
        companyId: companyId > 0 ? companyId : null,
        isActive: form.isActive,
      };

      const res = await client.post("/accounts", payload);

      setMessage(res.data?.message || "Account created");
      setForm(emptyForm);
      await load();
    } catch (e) {
      console.error("Accounts save error:", e);

      setMessage(
        e?.response?.data?.message ||
          e?.response?.data?.title ||
          e?.response?.data ||
          e.message ||
          "Failed to save account.",
      );
    } finally {
      setSaving(false);
    }
  };

  const badgeClass = (type) => {
    if (type === "Asset") return "text-bg-success";
    if (type === "Liability") return "text-bg-danger";
    if (type === "Income") return "text-bg-primary";
    if (type === "Expense") return "text-bg-warning";
    if (type === "Equity") return "text-bg-info";
    return "text-bg-secondary";
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-wallet2 me-2"></i>
            Accounts
          </h6>
          <div style={styles.sub}>
            Chart of accounts for finance and reports
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <div className="col-md-2">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                placeholder="Account Code"
                value={form.accountCode}
                onChange={(e) =>
                  setForm({ ...form, accountCode: e.target.value })
                }
              />
            </div>

            <div className="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                placeholder="Account Name"
                value={form.accountName}
                onChange={(e) =>
                  setForm({ ...form, accountName: e.target.value })
                }
              />
            </div>

            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.accountType}
                onChange={(e) =>
                  setForm({ ...form, accountType: e.target.value })
                }
              >
                <option>Asset</option>
                <option>Liability</option>
                <option>Equity</option>
                <option>Income</option>
                <option>Expense</option>
              </select>
            </div>

            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.parentAccountId}
                onChange={(e) =>
                  setForm({ ...form, parentAccountId: e.target.value })
                }
              >
                <option value="">No Parent Account</option>
                {rows.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getCode(a)} - {getName(a)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={save}
                disabled={saving}
              >
                <i className="bi bi-check2-circle me-1"></i>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>

          {message && (
            <div
              className="alert alert-info py-2 mb-2"
              style={{ fontSize: 11 }}
            >
              {String(message)}
            </div>
          )}

          <div
            style={{
              fontSize: 10,
              color: "#6c757d",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              Total Accounts: <strong>{rows.length}</strong>
            </span>
            <span>{loading ? "Loading..." : "Account master"}</span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  <th style={{ ...styles.th, minWidth: 120 }}>Code</th>
                  <th style={{ ...styles.th, minWidth: 260 }}>Account Name</th>
                  <th style={{ ...styles.th, minWidth: 120 }}>Type</th>
                  <th style={{ ...styles.th, minWidth: 220 }}>
                    Parent Account
                  </th>
                  <th style={{ ...styles.th, minWidth: 90 }}>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      Loading accounts...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No accounts found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const type = getType(r);
                    const parentId = getParentId(r);

                    const parent = rows.find(
                      (x) => String(getId(x)) === String(parentId),
                    );

                    return (
                      <tr key={getId(r) ?? i}>
                        <td style={{ ...styles.td, fontWeight: 700 }}>
                          {getCode(r)}
                        </td>

                        <td style={styles.td}>{getName(r)}</td>

                        <td style={styles.td}>
                          <span
                            className={`badge ${badgeClass(type)}`}
                            style={styles.badge}
                          >
                            {type}
                          </span>
                        </td>

                        <td style={styles.td}>
                          {parent
                            ? `${getCode(parent)} - ${getName(parent)}`
                            : "-"}
                        </td>

                        <td style={styles.td}>
                          {getIsActive(r) ? (
                            <span
                              className="badge text-bg-success"
                              style={styles.badge}
                            >
                              Active
                            </span>
                          ) : (
                            <span
                              className="badge text-bg-secondary"
                              style={styles.badge}
                            >
                              Inactive
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
